import { Component, OnInit, ViewChild, AfterViewInit, inject, OnDestroy, computed, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { LoginService, UserDetails } from '@services/login.service';
import { UtilService } from '@services/util.service';
import { LogService } from '@services/log.service';
import { PreferencesService } from '@services/preferences.service';
import { ConsentService } from '@services/consent.service';
import { Observable, map, catchError, debounceTime, distinctUntilChanged, filter, of, switchMap, tap, Subject, takeUntil, startWith, firstValueFrom } from 'rxjs';
import { AuditDetails, CompanyPersonnel, LoginType } from '@models/registration.models';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { environment } from '@environments/environment';
import { AddCompanyDialogComponent, AddCompanyDialogData, AddCompanyDialogResult } from './add-company-dialog.component';
import { EmailVerificationPopupComponent } from './email-verification-popup.component';
import { EmailAliasConfirmationDialogComponent } from './email-alias-confirmation-dialog.component';
import { UNKNOWN_COMPANY_ID } from '@constants/app.constants';

interface ContactOption {
  id: string;
  displayName: string;
  personnel?: CompanyPersonnel;
  isAddNew?: boolean;
}

@Component({
  selector: 'app-registration',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatProgressBarModule,
    MatRadioModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    ReactiveFormsModule
  ],
})
export class RegisterComponent implements OnInit, AfterViewInit, OnDestroy {

  // View child for stepper
  @ViewChild('stepper') stepper!: MatStepper;

  router = inject(Router);
  private dialog = inject(MatDialog);
  activatedRoute = inject(ActivatedRoute);
  private consentService = inject(ConsentService);
  
  registrationForm: FormGroup;
  registrationInProgress = false;
  filteredCompanies$: Observable<{id: string, name: string}[]> = new Observable();
  fetchingCompanyDetails = false;
  validCompanies: {id: string, name: string, type: string}[] = [];
  selectedCompany: {id: string, name: string, type: string} | null = null;
  companyPersonnel: CompanyPersonnel[] = [];
  availableContacts: ContactOption[] = [];

  private isSdkFlow = false;

  // Set when the user arrived through a valid magic link (/register?link=...), i.e. the email is
  // already proven to belong to them. Registration then skips the login email and re-enters /auth.
  private verifiedLinkEmail: string | null = null;
  
  // Subject to control when to stop the company search stream
  private stopCompanySearch$ = new Subject<void>();
  
  // Company selection edit mode
  companyEditMode = true;
  
  // Signal for typed company name - more efficient than function calls
  private typedCompanyNameSignal = signal<string>('');
  typedCompanyName = computed(() => this.typedCompanyNameSignal());
  
  // Computed signal to check if typed company is in list
  isTypedCompanyInList = computed(() => {
    const typedName = this.typedCompanyName();
    if (!typedName || !this.validCompanies) {
      return false;
    }
    return this.validCompanies.some(company => 
      company.name.toLowerCase() === typedName.toLowerCase()
    );
  });
  
  // Email domain validation properties
  validatingEmailDomain = false;
  emailDomainValidation: {isValid: boolean, message?: string} | null = null;
  showDomainWarning = false;

  constructor(private fb: FormBuilder, private loginService: LoginService, private util: UtilService, private logService: LogService, private preferencesService: PreferencesService) {
    const userDetails: UserDetails | null = this.loginService.loginUser;

    // Check for router state data early in constructor
    const routerState = history.state as { userDetails?: any } | undefined;
    console.log('Router state in constructor:', routerState);

    this.registrationForm = this.fb.group({
      // Step 1: Registration Type
      registeringAs: ['', Validators.required],

      // Step 2: Personal details
      personalDetails: this.fb.group({
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        phone: ['', [Validators.pattern('^((\\+44\\s?7\\d{3}|\\(?07\\d{3}\\)?)\\s?\\d{3}\\s?\\d{3}$|(?:\\+44\\s?2\\d{2}|0(?:20|2\\d{2}))\\s?\\d{4}\\s?\\d{4}$|(?:\\+44\\s?1\\d{3}|01\\d{3})\\s?\\d{3}\\s?\\d{3,4}$)')]], 
        companyName: [''], // Only required if registering as company
        companyId: [''], // Hidden field to store selected company ID
        companyType: [''], // Hidden field to store selected company type
        contactId: ['', {disabled: true}], // Field to store selected contact ID
        role: [''], // Only required if registering as company
      }),

      // Step 3: Terms and conditions
      agreeToTerms1: [false, Validators.requiredTrue],
      agreeToTerms1Timestamp: [null],
    });
    
    //Check if user exists and is unverified
    // if (userDetails && userDetails.email && userDetails.isVerified === false) {
    //   // Set registeringAs to 'company' and disable it
    //   const registeringAsControl = this.registrationForm.get('registeringAs');
    //   if (registeringAsControl) {
    //     // Set value and validate before disabling
    //     registeringAsControl.setValue('company', { emitEvent: false });
       
    //     // Removing the disabling to support sole-trader registration
    //     // registeringAsControl?.disable({ emitEvent: false });
    //   }
      
    //   // Set email from userDetails
    //   const emailControl = this.registrationForm.get('personalDetails.email');
    //   if (emailControl) {
    //     // Set the email value
    //     emailControl.setValue(userDetails.email);
    //     // Disable the email field from editing
    //     emailControl.disable();
    //   }
    // }
  }

  // Custom validator to ensure company is selected from autocomplete
  private companySelectionValidator(validCompanies: {id: string, name: string}[]): (control: AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }
      
      const isValidCompany = validCompanies.some(company => company.name === control.value);
      return isValidCompany ? null : { invalidCompany: { value: control.value } };
    };
  }

  private companyNameValueChangesListener(): void {
    this.filteredCompanies$ = this.registrationForm.get('personalDetails.companyName')?.valueChanges
      .pipe(
        // tap((value) => console.log('Company name input changed', value)),
        debounceTime(300),
        distinctUntilChanged(),
        filter((value: string) => !!value && value.trim().length >= 3),
        takeUntil(this.stopCompanySearch$), // Stop the stream when stopCompanySearch$ emits
        tap(() => {
          this.fetchingCompanyDetails = true;
          this.availableContacts = []; // Clear contacts when searching for companies
          this.validCompanies = []; // Clear previous search results
        }),
        switchMap(value => {
          if (value === this.selectedCompany?.name) {
            // If the input value matches the selected company, return the selected company
            return of([{ id: this.selectedCompany.id, name: this.selectedCompany.name }]);
          } else {
            return this.util.searchCompanies(value).pipe(
              takeUntil(this.stopCompanySearch$), // Also stop the API call itself
              map((companies) => {
                return companies.map(company => ({
                  id: company.number,
                  name: company.name,
                  type: company.type || 'ltd' // Default to 'ltd' if type is not provided
                }));
              }),
              tap((companies) => {
                this.fetchingCompanyDetails = false;
                this.validCompanies = companies;
                this.updateCompanyValidation();
              }),
              catchError((error) => {
                this.fetchingCompanyDetails = false;
                console.error('Error fetching company details:', error);
                this.validCompanies = [];
                this.updateCompanyValidation();
                this.logService.addErrorLog('COMPANY_SEARCH', {key: value, error});
                return of([]);
              })
            )
          }
        })
      ) as Observable<{id: string, name: string}[]>;
  }

  private restartCompanySearch(): void {
    // Reset the stop subject to allow the stream to work again
    this.stopCompanySearch$ = new Subject<void>();
    // Restart the company name value changes listener
    this.companyNameValueChangesListener();
  }

  enableCompanyEditMode(): void {
    this.companyEditMode = true;
    this.selectedCompany = null;
    
    // Clear company-related form values
    this.registrationForm.get('personalDetails.companyId')?.setValue('');
    this.registrationForm.get('personalDetails.companyType')?.setValue('');
    this.registrationForm.get('personalDetails.companyName')?.enable();
    this.registrationForm.get('personalDetails.companyName')?.setValue('');
    
    // Clear contacts and personnel
    this.availableContacts = [];
    this.companyPersonnel = [];
    this.registrationForm.get('personalDetails.contactId')?.setValue('');
    
    // Restart the company search
    this.restartCompanySearch();
    
    // Update validation
    this.updateCompanyValidation();
  }

  ngOnInit() {

      // Initialize the typed company name signal
      const initialCompanyName = this.registrationForm.get('personalDetails.companyName')?.value || '';
      this.typedCompanyNameSignal.set(initialCompanyName);

      this.activatedRoute.queryParams.subscribe((params: Params) => {
        // Handle query params if needed
        if (params['sdkFlow']) {
          this.isSdkFlow = true;
          // const email = params['email'];
          // this.registrationForm.get('personalDetails.email')?.setValue(email);
          // this.registrationForm.get('personalDetails.email')?.disable();

          // this.registrationForm.get('registeringAs')?.reset();
        }
      });


      // Resolve who this is: from a magic link (?link=) and/or a consent request (?consentRequestId=)
      this.resolveEntry();

       // Log page view
      this.logService.logPageView('register', {
        fromMagicLink: !!this.activatedRoute.snapshot.queryParams['link'],
        queryParams: this.activatedRoute.snapshot.queryParams
      });

      // Set up company name autocomplete
      this.companyNameValueChangesListener();

      this.registrationForm.get('agreeToTerms1')?.valueChanges.subscribe(event => {
        this.onTermsChange(event, 'agreeToTerms1');
      });

      this.registrationForm.get('registeringAs')?.valueChanges.subscribe((event) => {
        // Reset company selection when switching registration type
        if (event !== 'company') {
          this.selectedCompany = null;
          this.companyEditMode = true;
          this.registrationForm.get('personalDetails.companyId')?.setValue('');
          this.registrationForm.get('personalDetails.companyName')?.setValue('');
          this.registrationForm.get('personalDetails.companyName')?.enable();
          this.registrationForm.get('personalDetails.companyType')?.setValue('');
          this.availableContacts = [];
          this.companyPersonnel = [];
        } else {
          // If switching to company registration, enable edit mode and restart search
          this.companyEditMode = true;
          this.registrationForm.get('personalDetails.companyName')?.enable();
          this.restartCompanySearch();
        }
        this.updateCompanyValidation();
      });

      // Also restart company search when company name is cleared manually in edit mode
      this.registrationForm.get('personalDetails.companyName')?.valueChanges.subscribe((value) => {
        // Update the signal with the new value
        this.typedCompanyNameSignal.set(value || '');
        
        if (this.companyEditMode && (!value || value.trim() === '')) {
          // If company name is cleared in edit mode, restart the search and reset selection
          this.selectedCompany = null;
          this.registrationForm.get('personalDetails.companyId')?.setValue('');
          this.registrationForm.get('personalDetails.companyType')?.setValue('');
          this.availableContacts = [];
          this.companyPersonnel = [];
          this.restartCompanySearch();
        }
      });
  }

  ngAfterViewInit() {
    this.jumpToFirstIncompleteStep();
  }

  /**
   * Opens the stepper at the first step that still needs input: step 2 once the account type is known,
   * step 3 once the personal details are valid too. Runs after the view initialises and again once an
   * async prefill (resolveEntry) has filled the form.
   */
  private jumpToFirstIncompleteStep(): void {
    if (this.registrationForm.get('registeringAs')?.valid) {
      let updatedStepIndex = 1;

      const registeringAs = this.registrationForm.get('registeringAs')?.value;

      // Email domain validation status is set to true for individuals
      let emailDomainValidationStatus = registeringAs === 'company' ? this.emailDomainValidation?.isValid : true;

      if (this.registrationForm.get('personalDetails')?.valid && emailDomainValidationStatus) {
        updatedStepIndex = 2;
      }
      setTimeout(() => {
        if (this.stepper) {
          this.stepper.selectedIndex = updatedStepIndex; // 0-based: 1 = step 2, 2 = step 3
        }
      });
    }
  }

  onTermsChange(event: any, controlName: string) {
    if (event) {
      this.registrationForm.get(controlName + 'Timestamp')?.setValue(new Date().toISOString());
    } else {
      this.registrationForm.get(controlName + 'Timestamp')?.setValue(null);
    }
  }

  private updateCompanyValidation() {
    const companyNameControl = this.registrationForm.get('personalDetails.companyName');
    const roleControl = this.registrationForm.get('personalDetails.role');
    if (this.isCompanySelected()) {
      companyNameControl?.setValidators([
        Validators.required,
        this.companySelectionValidator(this.validCompanies)
      ]);
      roleControl?.setValidators([Validators.required]);
    } else {
      companyNameControl?.clearValidators();
      roleControl?.clearValidators();
    }
    companyNameControl?.updateValueAndValidity();
    roleControl?.updateValueAndValidity();
  }

  /**
   * Reset form with proper validator clearing and value initialization
   * Unlike form.reset() which sets values to null, this sets them to empty strings
   * and clears dynamically added validators
   */
  private resetForm(): void {
    // Clear dynamic validators for company-related fields
    const companyNameControl = this.registrationForm.get('personalDetails.companyName');
    const roleControl = this.registrationForm.get('personalDetails.role');
    
    companyNameControl?.clearValidators();
    roleControl?.clearValidators();
    companyNameControl?.updateValueAndValidity();
    roleControl?.updateValueAndValidity();

    // Reset form values to empty strings instead of null
    this.registrationForm.reset({
      registeringAs: '',
      personalDetails: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        companyName: '',
        companyId: '',
        companyType: '',
        contactId: '',
        role: ''
      },
      agreeToTerms1: false,
      agreeToTerms1Timestamp: null
    });

    // Reset form state
    this.registrationForm.markAsUntouched();
    this.registrationForm.markAsPristine();

    // Reset component state
    this.selectedCompany = null;
    this.companyEditMode = true;
    this.validCompanies = [];
    this.availableContacts = [];
    this.companyPersonnel = [];
    this.emailDomainValidation = null;
    this.showDomainWarning = false;
    this.typedCompanyNameSignal.set('');
  }

  // Common function to set "Add new contact" as single option and auto-select it
  private setAddNewContactAsOnlyOption(canClearPersonalFields = true): void {
    this.availableContacts = [{
      id: 'add_new',
      displayName: 'Add new contact',
      isAddNew: true
    }];
    
    // Auto-select the "Add new contact" option
    this.registrationForm.get('personalDetails.contactId')?.setValue('add_new');
    
    // Clear personal details fields for new contact entry
    if (canClearPersonalFields) {
      this.clearPersonalDetailsFields();
    }
  }

  onCompanySelected(selectedCompany: {id: string, name: string, type: string}, canClearPersonalFields = true) {
    // Stop the company search stream to prevent further API calls
    this.stopCompanySearch$.next();

    this.selectedCompany = selectedCompany;
    this.companyEditMode = false; // Exit edit mode
    
    // Disable the company name control
    this.registrationForm.get('personalDetails.companyName')?.disable();
    
    this.registrationForm.get('contactId')?.enable(); // Enable contact selection when a company is selected
    this.registrationForm.get('personalDetails.companyId')?.setValue(selectedCompany.id);
    this.registrationForm.get('personalDetails.companyName')?.setValue(selectedCompany.name);
    this.registrationForm.get('personalDetails.companyType')?.setValue(selectedCompany.type);
    
    // Reset contact selection when company changes
    this.registrationForm.get('personalDetails.contactId')?.setValue('');
    this.availableContacts = [];
    this.companyPersonnel = [];

    this.logService.addRegisterLog({type: 'COMPANY_SELECTED', companyId: selectedCompany.id, companyName: selectedCompany.name});
    
    // For custom companies , skip getCompanyPersonnel and just provide "Add new contact"
    if (selectedCompany.id === UNKNOWN_COMPANY_ID) {
      this.setAddNewContactAsOnlyOption(canClearPersonalFields);
      this.fetchingCompanyDetails = false;
      
      // Clear validation errors since we're accepting the custom company
      const companyNameControl = this.registrationForm.get('personalDetails.companyName');
      if (companyNameControl) {
        companyNameControl.setErrors(null);
      }
    } else {
      // For regular companies, fetch company personnel details
      this.fetchingCompanyDetails = true;
      this.util.getCompanyPersonnel(selectedCompany.id, selectedCompany.type).subscribe({
        next: (response) => {
          if (response.data && response.data.length > 0) {
            this.companyPersonnel = response.data;
            
            // Create contact options from personnel data
            this.availableContacts = response.data.map((personnel, index) => ({
              id: `personnel_${index}`,
              displayName: `${personnel.firstName} ${personnel.lastName}`,
              personnel: personnel
            }));
            
            // Add "Add new Contact" option
            this.availableContacts.push({
              id: 'add_new',
              displayName: 'Add new contact',
              isAddNew: true
            });
          } else {
            // If no personnel found, only show "Add new Contact" option and auto-select it
            this.setAddNewContactAsOnlyOption(canClearPersonalFields);
          }
          this.fetchingCompanyDetails = false;
        },
        error: (error) => {
          console.error('Error fetching company personnel:', error);
          this.fetchingCompanyDetails = false;
          // On error, still provide "Add new Contact" option and auto-select it
          this.setAddNewContactAsOnlyOption(canClearPersonalFields);
          this.logService.addErrorLog('COMPANY_PERSONNEL_FETCH', {companyId: selectedCompany.id, error});
        }
      });
    }
  }

  // Method to show "Tell us about your company" dialog
  showAddCompanyDialog(): void {
    const currentCompanyName = this.registrationForm.get('personalDetails.companyName')?.value || '';
    
    const dialogRef = this.dialog.open(AddCompanyDialogComponent, {
      width: '450px',
      data: {
        companyName: currentCompanyName
      } as AddCompanyDialogData
    });

    dialogRef.afterClosed().subscribe((result: AddCompanyDialogResult) => {
      if (result) {
        // Create a custom company object
        const customCompany = {
          id: UNKNOWN_COMPANY_ID,
          name: result.companyName,
          type: result.companyType
        };
        
        // Call the unified onCompanySelected method
        this.onCompanySelected(customCompany);
      }
    });
  }

  // Check if the company name field has invalidCompany error
  hasInvalidCompanyError(): boolean {
    const companyNameControl = this.registrationForm.get('personalDetails.companyName');
    return !!(companyNameControl?.errors?.['invalidCompany'] && companyNameControl?.touched);
  }

  onContactSelected(contactId: string) {
    const selectedContact = this.availableContacts.find(contact => contact.id === contactId);

    this.logService.addRegisterLog({type: 'CONTACT_SELECTED', contact: selectedContact });
    
    if (selectedContact?.isAddNew) {
      // Clear form fields for new contact entry
      this.clearPersonalDetailsFields();
    } else if (selectedContact?.personnel) {
      // Prefill form with selected personnel details
      const personnel = selectedContact.personnel;
      const emailControl = this.registrationForm.get('personalDetails.email');
      
      this.registrationForm.get('personalDetails.firstName')?.setValue(personnel.firstName);
      this.registrationForm.get('personalDetails.lastName')?.setValue(personnel.lastName);
      
      // Only update email if the email field is not disabled (for unverified users)
      if (emailControl && !emailControl.disabled) {
        emailControl.setValue(personnel.email || '');
      }
      
      this.registrationForm.get('personalDetails.phone')?.setValue(personnel.phone || '');
      this.registrationForm.get('personalDetails.role')?.setValue(personnel.jobTitle || '');
    }
  }

  private clearPersonalDetailsFields() {
    const emailControl = this.registrationForm.get('personalDetails.email');
    
    this.registrationForm.get('personalDetails.firstName')?.setValue('');
    this.registrationForm.get('personalDetails.lastName')?.setValue('');
    
    // Only clear email if it's not disabled (for unverified users)
    if (emailControl && !emailControl.disabled) {
      emailControl.setValue('');
    }
    
    this.registrationForm.get('personalDetails.phone')?.setValue('');
    this.registrationForm.get('personalDetails.role')?.setValue('');
  }

  /**
   * Entry resolution for /register. Inputs (query params):
   *  - link: magic link from /auth for an unverified user (proves email ownership)
   *  - consentRequestId: direct entry from a consent request
   * Existing verified users are sent on to sign-in (or meter-details when already signed in);
   * everyone else gets the form prefilled from the consent request.
   */
  private async resolveEntry(): Promise<void> {
    const params = this.activatedRoute.snapshot.queryParams;
    const link = params['link'];
    let consentRequestId: string | undefined = params['consentRequestId'];
    let linkEmail: string | undefined;

    if (link) {
      const decoded = this.loginService.decodeAuthLink(link);
      if (!decoded?.email) {
        this.router.navigate(['/signin']);
        return;
      }
      linkEmail = decoded.email;
      consentRequestId = consentRequestId || decoded.consentRequestId;
    }

    if (!consentRequestId) {
      if (linkEmail) {
        this.verifiedLinkEmail = linkEmail.toLowerCase();
        await this.prefillFormWithUserDetails({ email: linkEmail, signUpType: 'company' });
        this.jumpToFirstIncompleteStep();
      }
      return;
    }

    let consentRequest: any = null;
    try {
      consentRequest = await firstValueFrom(this.consentService.getConsentRequestById(consentRequestId));
    } catch (error) {
      console.error('Failed to fetch consent request', error);
    }

    const customerEmail: string | undefined = consentRequest?.customerEmail;
    if (!customerEmail || (linkEmail && linkEmail.toLowerCase() !== customerEmail.toLowerCase())) {
      console.error('Consent request missing or does not match the magic link email');
      this.router.navigate(['/signin']);
      return;
    }

    const existingUser = await this.loginService.getUserDetails(customerEmail).catch(() => null);
    if (existingUser?.isVerified) {
      if (linkEmail) {
        // Link is valid and the user is already registered - let /auth sign them in
        this.router.navigate(['/auth'], { queryParams: { link, consentRequestId } });
      } else if (await this.isSignedInAs(customerEmail)) {
        this.router.navigate(['/meter-details'], { queryParams: { consentRequestId } });
      } else {
        this.router.navigate(['/signin'], { queryParams: { email: customerEmail, consentRequestId } });
      }
      return;
    }

    if (linkEmail) {
      this.verifiedLinkEmail = linkEmail.toLowerCase();
    }
    await this.prefillFormWithUserDetails({
      // SDK registrations let the user pick the type unless the consent request says otherwise
      ...(!this.isSdkFlow && { signUpType: 'company' }),
      ...consentRequest.customConfig?.userDetails,
      email: customerEmail,
    });
    this.jumpToFirstIncompleteStep();
  }

  private async isSignedInAs(email: string): Promise<boolean> {
    return (await this.loginService.refreshUserFromCognito())
      && this.loginService.loginUser?.email?.toLowerCase() === email.toLowerCase();
  }

  private async prefillFormWithUserDetails(userDetails: any) {
    if (!userDetails) return;

    if (userDetails.email) {
      this.registrationForm.get('personalDetails.email')?.setValue(userDetails.email);
      this.registrationForm.get('personalDetails.email')?.disable();
    }

    // Prefill first name
    if (userDetails.firstName) {
      this.registrationForm.get('personalDetails.firstName')?.setValue(userDetails.firstName);
    }

    // Prefill last name  
    if (userDetails.lastName) {
      this.registrationForm.get('personalDetails.lastName')?.setValue(userDetails.lastName);
    }

    // Prefill phone number
    if (userDetails.phoneNumber) {
      this.registrationForm.get('personalDetails.phone')?.setValue(userDetails.phoneNumber);
    }

    // Set registration type based on signUpType
    if (userDetails.signUpType) {
      this.registrationForm.get('registeringAs')?.setValue(userDetails.signUpType, { emitEvent: false });
    }

    // Set role if available (top level)
    if (userDetails.role) {
      this.registrationForm.get('personalDetails.role')?.setValue(userDetails.role);
    }

    // Handle company details if they exist
    if (userDetails.companyDetails && userDetails.signUpType === 'company') {
      const companyDetails = userDetails.companyDetails;
      
      // Set company name if available
      if (companyDetails.companyName) {
        this.registrationForm.get('personalDetails.companyName')?.setValue(companyDetails.companyName);
        
        // Create a custom company object to simulate company selection
        const customCompany = {
          id: companyDetails.companyNumber || UNKNOWN_COMPANY_ID,
          name: companyDetails.companyName,
          type: companyDetails.type || 'ltd'
        };
        
        // Select the company
         this.onCompanySelected(customCompany, false);

        let email = this.registrationForm.get('personalDetails.email')?.value;
        if (email && this.selectedCompany?.name) {
          const validationResult = await firstValueFrom(this.util.validateEmailDomainWithCompany(email, this.selectedCompany.name))
          this.emailDomainValidation = validationResult;
        }
      }
    }
  }

  displayCompanyName(company: {id: string, name: string} | string): string {
    if (typeof company === 'string') {
      return company;
    }
    return company?.name || '';
  }

  isCompanySelected(): boolean {
    return this.registrationForm.get('registeringAs')?.value === 'company';
  }

  canShowCompanyEditButton(): boolean {
    return this.isCompanySelected() && !this.companyEditMode && this.selectedCompany !== null;
  }

  hasAvailableContacts(): boolean {
    return this.availableContacts.length > 0;
  }

  isUserDetailsValid(): boolean {
    return this.registrationForm.get('personalDetails')?.valid || false;
  }

  allStepsValid(): boolean {
    return this.registrationForm.valid;
  }

  onTest() {
    console.log(this.registrationForm.value);
    this.openEmailVerificationPopup('santhosh.r@digitalapi.ai');
  }

  onSubmit() {
    if (!this.allStepsValid() || this.registrationInProgress) {
      return;
    }

    const emailControl = this.registrationForm.get('personalDetails.email');
    const email = emailControl?.value as string;
    const atIndex = email.indexOf('@');
    const plusIndex = email.indexOf('+');

    if (plusIndex > 0 && plusIndex < atIndex) {
      const accountEmail = email.slice(0, plusIndex) + email.slice(atIndex);
      const dialogRef = this.dialog.open(EmailAliasConfirmationDialogComponent, {
        data: {
          originalEmail: email,
          accountEmail,
          alias: email.slice(plusIndex, atIndex),
          emailEditable: !emailControl?.disabled,
          fromConsentRequest: this.activatedRoute.snapshot.queryParamMap.has('consentRequestId'),
        },
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === 'edit') {
          this.stepper.selectedIndex = 1;
        } else if (result === 'continue' && this.allStepsValid() && emailControl?.value === email) {
          this.submitRegistration();
        }
      });
      return;
    }

    this.submitRegistration();
  }

  private submitRegistration(): void {
    
    this.registrationInProgress = true;

    const personalDetails = this.registrationForm.get('personalDetails')?.getRawValue();
    const agreeToTerms1Timestamp = this.registrationForm.get('agreeToTerms1Timestamp')?.value;
    
    // One checkbox ("read and agree to ... the terms and conditions and the privacy policy") covers both.
    const accepted = this.registrationForm.get('agreeToTerms1')?.value;
    const auditDetails: AuditDetails = {
      terms: {
        version: environment.termsVersion,
        url: environment.termsUrl,
        viewedTimestamp: agreeToTerms1Timestamp,
        checkedTimestamp: agreeToTerms1Timestamp,
        method: 'clickwrap',
        accepted,
      },
      privacy: {
        version: environment.privacyPolicyVersion,
        url: environment.privacyPolicyUrl,
        viewedTimestamp: agreeToTerms1Timestamp,
        checkedTimestamp: agreeToTerms1Timestamp,
        method: 'clickwrap',
        accepted,
      }
    };
    
    console.log('Submitting registration:', personalDetails, auditDetails);

    // Email already proven through the magic link -> no login email, go back to /auth with the same link
    const isLoginUserUnverified = !!this.verifiedLinkEmail
      && personalDetails.email?.toLowerCase() === this.verifiedLinkEmail;

    let emailVerificationStatus = 'verified';

    if (this.emailDomainValidation && !this.emailDomainValidation.isValid) {
      // If email domain validation fails, set status to unvalidated
      emailVerificationStatus = 'unvalidated';
    }


    const queryParams: Params = {
      consentRequestId: this.activatedRoute.snapshot.queryParams['consentRequestId'] || null,
    };
    // Call the login service to register the user
    this.loginService.registerUser(personalDetails, auditDetails, emailVerificationStatus, isLoginUserUnverified, this.isSdkFlow, queryParams).subscribe({
        next: (success) => {
          this.stepper.selectedIndex = 0;
          this.registrationInProgress = false;
          this.resetForm();
          if (isLoginUserUnverified) {
            // Email proven by the magic link: no login email, re-enter the auth route that issued the link
            this.router.navigate([this.isSdkFlow ? '/sdk-auth' : '/auth'], { queryParamsHandling: 'merge' });
          } else {
            const loginOption = success.loginType === LoginType.EMAIL_MAGIC_LINK ? 'login link' : 'OTP';
            if (success.loginType === LoginType.EMAIL_MAGIC_LINK) {
              // Open email verification popup for email magic link
              this.openEmailVerificationPopup(personalDetails.email);
              this.util.openSnackBar(`Your wallet has been created successfully. Please check your email for the ${loginOption}.`, 'Close', 'success');
            } else if (success.loginType === LoginType.EMAIL_OTP) {
              this.util.openSnackBar(`Your wallet has been created successfully. Please check your email for the ${loginOption}.`, 'Close', 'success');
              // Redirect to OTP verification page
              this.router.navigate(['/signin'], { queryParams: { email: personalDetails.email, encryptedData: success.metadata, isSdkFlow: this.isSdkFlow } });
            } else if (success.loginType === LoginType.BYPASS_EMAIL_VERIFICATION) {
              // For bypass (trusted) verification, redirect to dashboard
              if (success.metadata) {
                window.location.href = success.metadata;
              } else {
                this.router.navigate(['/signin'], { queryParams: { email: personalDetails.email, encryptedData: success.metadata, isSdkFlow: this.isSdkFlow } });
              }
            }
          }
        },
        error: (error: any) => {
          console.error('Error registering:', error);
          this.registrationInProgress = false;
          this.util.showErrorMessageFromAPIResponse('Failed to create your wallet.', error);
        }
      }
    );
  }


  // Method to validate email domain with company
  validateEmailDomainWithCompany(): void {
    // Org preference: skip the domain-match check entirely for SDK-driven registrations
    if (this.isSdkFlow && this.preferencesService.shouldBypassEmailDomainValidation()) {
      this.showDomainWarning = false;
      this.emailDomainValidation = { isValid: true };
      this.stepper.next();
      return;
    }

    if (!this.isCompanySelected() || !this.selectedCompany) {
      // If not a company registration, proceed to next step
      if (!this.isCompanySelected()) {
        this.stepper.next();
      }
      return;
    }

    // If companyId is unknown (custom company), bypass validation and move to next step
    if (this.selectedCompany.id === UNKNOWN_COMPANY_ID) {
      this.showDomainWarning = false;
      this.emailDomainValidation = { isValid: false };
      this.stepper.next();
      return;
    }

    const email = this.registrationForm.get('personalDetails.email')?.value;
    if (!email || !email.includes('@')) {
      return;
    }

    this.validatingEmailDomain = true;
    this.emailDomainValidation = null;
    this.showDomainWarning = false;

    this.util.validateEmailDomainWithCompany(email, this.selectedCompany.name).subscribe({
      next: (result) => {
        this.validatingEmailDomain = false;
        this.emailDomainValidation = result;
        this.showDomainWarning = !result.isValid;
        
        // If validation passes, move to next step
        if (result.isValid) {
          this.stepper.next();
        }
        // If validation fails, stay on current step and show warning
      },
      error: (error) => {
        console.error('Error validating email domain:', error);
        this.validatingEmailDomain = false;
        // On error, allow user to proceed
        this.emailDomainValidation = { isValid: true };
        this.showDomainWarning = false;
        this.stepper.next();
      }
    });
  }

  // Method to proceed to next step (bypassing domain validation)
  proceedToNextStep(): void {
    this.showDomainWarning = false;
    // Manually move to next step
    if (this.stepper) {
      this.stepper.next();
    }
  }

  // Check if user can proceed to next step normally
  canProceedToNextStep(): boolean {
    return this.isUserDetailsValid() && !this.validatingEmailDomain;
  }

  ngOnDestroy(): void {
    // Complete the subject to avoid memory leaks
    this.stopCompanySearch$.complete();
  }

  // Method to open the email verification popup
  openEmailVerificationPopup(email: string): void {
    const dialogRef = this.dialog.open(EmailVerificationPopupComponent, {
      data: { email },
      disableClose: true,
      panelClass: 'email-verification-dialog',
      hasBackdrop: true,
      backdropClass: 'dark-backdrop'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'resend-email') {
        // Handle resend email logic here if needed
        this.loginService.sendMagicLink(email, environment.magicLinkRedirectUrl).subscribe({
          next: () => { 
            this.util.openSnackBar(`Email resent successfully to ${email}`, 'Close', 'success');
          },
          error: (error) => {
            // Show success message even on error for consistency
            this.util.openSnackBar(`Email resent successfully to ${email}`, 'Close', 'success');
          }
        });
      } else if (result === 'choose-another-method') {
        // Handle choosing another method if needed
        console.log('User chose another method');
      }
    });
  }
}
